import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse } from "../../../types/common";
import { IIntegration, IIntegrationConnectUrl, IIntegrationHubData } from "../../../types/api/integrations";
import {
  listIntegrations,
  getIntegrationConnectUrl,
  disconnectIntegration,
  listHubIntegrations,
  connectHubIntegration,
  testHubIntegration,
  disconnectHubIntegration,
  getXeroStatus,
  getXeroAuthUrl,
  disconnectXero,
  syncInvoiceToXero,
  syncClientToXero,
  getQBStatus,
  getQBAuthUrl,
  disconnectQB,
  syncInvoiceToQB,
  syncClientToQB,
  getGCalStatus,
  getGCalAuthUrl,
  disconnectGCal,
} from "./callbacks";
import { IXeroStatus, IXeroAuthUrl, IXeroSyncResult, IQBStatus, IQBAuthUrl, IQBSyncResult, IGCalStatus, IGCalAuthUrl } from "../../../types/api/integrations";

// ─── Legacy hooks ──────────────────────────────────────────────────────────────

export const useIntegrations = (
  options?: Omit<UseQueryOptions<TResponse<IIntegration[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IIntegration[]>, Error> => {
  return useQuery<TResponse<IIntegration[]>, Error>({
    queryKey: [API_ENDPOINTS.INTEGRATION_LIST],
    queryFn: async () => {
      const res = await listIntegrations();
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useIntegrationConnectUrl = (
  options?: Omit<
    UseMutationOptions<TResponse<IIntegrationConnectUrl>, unknown, string>,
    "mutationFn"
  >
): UseMutationResult<TResponse<IIntegrationConnectUrl>, unknown, string> => {
  return useMutation<TResponse<IIntegrationConnectUrl>, unknown, string>({
    mutationFn: async (provider: string) => {
      const res = await getIntegrationConnectUrl(provider);
      return res.data;
    },
    ...options,
  });
};

export const useDisconnectIntegration = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<null>, unknown, number> = {}
): UseMutationResult<TResponse<null>, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<null>, unknown, number>({
    mutationFn: async (id: number) => {
      const res = await disconnectIntegration(id);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INTEGRATION_LIST] });
      onSuccess?.();
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

// ─── Hub hooks (self-service Integration Marketplace) ─────────────────────────

export const useIntegrationHub = (
  options?: Omit<UseQueryOptions<TResponse<IIntegrationHubData>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IIntegrationHubData>, Error> => {
  return useQuery<TResponse<IIntegrationHubData>, Error>({
    queryKey: ['integrations', 'hub'],
    queryFn: async () => {
      const res = await listHubIntegrations();
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useConnectIntegration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, api_key, config }: { slug: string; api_key?: string; config?: Record<string, any> }) =>
      connectHubIntegration(slug, { api_key, config }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'hub'] }),
  });
};

export const useTestIntegration = () =>
  useMutation({
    mutationFn: (slug: string) => testHubIntegration(slug),
  });

export const useDisconnectHubIntegration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => disconnectHubIntegration(slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'hub'] }),
  });
};

// ─── Xero hooks ────────────────────────────────────────────────────────────────

export const useXeroStatus = (
  options?: Omit<UseQueryOptions<TResponse<IXeroStatus>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IXeroStatus>, Error> => {
  return useQuery<TResponse<IXeroStatus>, Error>({
    queryKey: ['xero', 'status'],
    queryFn: async () => {
      const res = await getXeroStatus();
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
};

export const useXeroAuthUrl = (): UseMutationResult<TResponse<IXeroAuthUrl>, unknown, void> => {
  return useMutation<TResponse<IXeroAuthUrl>, unknown, void>({
    mutationFn: async () => {
      const res = await getXeroAuthUrl();
      return res.data;
    },
  });
};

export const useXeroDisconnect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectXero(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['xero'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'hub'] });
    },
  });
};

export const useSyncInvoiceToXero = (): UseMutationResult<TResponse<IXeroSyncResult>, unknown, number> => {
  return useMutation<TResponse<IXeroSyncResult>, unknown, number>({
    mutationFn: async (invoiceId: number) => {
      const res = await syncInvoiceToXero(invoiceId);
      return res.data;
    },
  });
};

export const useSyncClientToXero = (): UseMutationResult<TResponse<IXeroSyncResult>, unknown, number> => {
  return useMutation<TResponse<IXeroSyncResult>, unknown, number>({
    mutationFn: async (clientId: number) => {
      const res = await syncClientToXero(clientId);
      return res.data;
    },
  });
};

// ─── QuickBooks hooks ──────────────────────────────────────────────────────────

export const useQBStatus = (
  options?: Omit<UseQueryOptions<TResponse<IQBStatus>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IQBStatus>, Error> => {
  return useQuery<TResponse<IQBStatus>, Error>({
    queryKey: ["quickbooks", "status"],
    queryFn: async () => {
      const res = await getQBStatus();
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
};

export const useQBAuthUrl = (): UseMutationResult<TResponse<IQBAuthUrl>, unknown, void> => {
  return useMutation<TResponse<IQBAuthUrl>, unknown, void>({
    mutationFn: async () => {
      const res = await getQBAuthUrl();
      return res.data;
    },
  });
};

export const useQBDisconnect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectQB(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quickbooks"] });
      qc.invalidateQueries({ queryKey: ["integrations", "hub"] });
    },
  });
};

export const useSyncInvoiceToQB = (): UseMutationResult<TResponse<IQBSyncResult>, unknown, number> => {
  return useMutation<TResponse<IQBSyncResult>, unknown, number>({
    mutationFn: async (invoiceId: number) => {
      const res = await syncInvoiceToQB(invoiceId);
      return res.data;
    },
  });
};

export const useSyncClientToQB = (): UseMutationResult<TResponse<IQBSyncResult>, unknown, number> => {
  return useMutation<TResponse<IQBSyncResult>, unknown, number>({
    mutationFn: async (clientId: number) => {
      const res = await syncClientToQB(clientId);
      return res.data;
    },
  });
};

// ─── Google Calendar hooks ─────────────────────────────────────────────────────

export const useGCalStatus = (
  options?: Omit<UseQueryOptions<TResponse<IGCalStatus>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IGCalStatus>, Error> => {
  return useQuery<TResponse<IGCalStatus>, Error>({
    queryKey: ["google-calendar", "status"],
    queryFn: async () => {
      const res = await getGCalStatus();
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
};

export const useGCalAuthUrl = (): UseMutationResult<TResponse<IGCalAuthUrl>, unknown, void> => {
  return useMutation<TResponse<IGCalAuthUrl>, unknown, void>({
    mutationFn: async () => {
      const res = await getGCalAuthUrl();
      return res.data;
    },
  });
};

export const useGCalDisconnect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectGCal(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["google-calendar"] });
      qc.invalidateQueries({ queryKey: ["integrations", "hub"] });
    },
  });
};
